import { msalInstance, apiScopes, API_BASE } from "../auth/msalConfig";
import type {
  MeResponse,
  Category,
  CostCentre,
  ExpenseRequestSummary,
  ExpenseRequestDetail,
  ExpenseLine,
  MileageRate,
  NewLineInput,
} from "./types";

async function getToken(): Promise<string> {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) throw new Error("Not signed in");
  const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
  return result.accessToken;
}

async function call<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail ?? j.title ?? detail;
    } catch {}
    throw new Error(`${res.status}: ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export interface UserSearchResult {
  oid: string;
  displayName: string;
  email: string;
}

export interface ManagerOverride {
  id: number;
  claimant_entra_oid: string;
  claimant_email: string;
  claimant_display_name: string;
  override_manager_oid: string;
  override_manager_email: string;
  override_manager_display_name: string;
  reason: string;
  set_by_email: string;
  set_at: string;
  active: boolean;
}

export const api = {
  me: () => call<MeResponse>("/api/me"),
  categories: () => call<Category[]>("/api/categories"),
  costCentres: () => call<CostCentre[]>("/api/cost-centres"),
  searchUsers: (q: string) => call<UserSearchResult[]>(`/api/users/search?q=${encodeURIComponent(q)}`),

  expenses: {
    list: (params: { status?: string; from?: string; to?: string; page?: number } = {}) => {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => v != null && qs.set(k, String(v)));
      return call<{ items: ExpenseRequestSummary[]; page: number; page_size: number }>(
        `/api/expenses${qs.toString() ? `?${qs}` : ""}`,
      );
    },
    get: (id: string) => call<ExpenseRequestDetail>(`/api/expenses/${id}`),
    create: (body: { cost_centre_id: number; manager_entra_oid?: string | null; manager_email?: string | null; manager_overridden?: boolean; manager_override_reason?: string | null }) =>
      call<ExpenseRequestSummary>("/api/expenses", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Partial<{ cost_centre_id: number; manager_entra_oid: string | null; manager_email: string | null; manager_overridden: boolean; manager_override_reason: string | null }>) =>
      call<ExpenseRequestSummary>(`/api/expenses/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    addLine: (id: string, line: NewLineInput) =>
      call<ExpenseLine>(`/api/expenses/${id}/lines`, { method: "POST", body: JSON.stringify(line) }),
    updateLine: (id: string, lineId: string, line: NewLineInput) =>
      call<ExpenseLine>(`/api/expenses/${id}/lines/${lineId}`, { method: "PUT", body: JSON.stringify(line) }),
    deleteLine: (id: string, lineId: string) =>
      call<void>(`/api/expenses/${id}/lines/${lineId}`, { method: "DELETE" }),
    submit: (id: string) => call<ExpenseRequestSummary>(`/api/expenses/${id}/actions/submit`, { method: "POST", body: "{}" }),
    recall: (id: string, reason?: string) =>
      call<{ status: string }>(`/api/expenses/${id}/actions/recall`, { method: "POST", body: JSON.stringify({ reason }) }),
    attachmentDownloadUrl: (id: string, attachmentId: string) =>
      call<{ url: string; file_name: string; content_type: string }>(`/api/expenses/${id}/attachments/${attachmentId}/download`),
  },

  admin: {
    listCategories: () => call<Category[]>("/api/manage/categories"),
    createCategory: (body: Partial<Category>) =>
      call<Category>("/api/manage/categories", { method: "POST", body: JSON.stringify(body) }),
    updateCategory: (id: number, body: Partial<Category>) =>
      call<Category>(`/api/manage/categories/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    deactivateCategory: (id: number) =>
      call<void>(`/api/manage/categories/${id}`, { method: "DELETE" }),

    listPolicy: () => call<{ key: string; value: string; updated_at: string }[]>("/api/manage/policy"),
    setPolicy: (key: string, value: string) =>
      call<{ key: string; value: string; updated_at: string }>(`/api/manage/policy/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ value }) }),

    listMileageRates: () =>
      call<MileageRate[]>("/api/manage/mileage-rates"),
    createMileageRate: (body: Partial<MileageRate>) =>
      call<MileageRate>("/api/manage/mileage-rates", { method: "POST", body: JSON.stringify(body) }),
    updateMileageRate: (id: number, body: Partial<MileageRate>) =>
      call<MileageRate>(`/api/manage/mileage-rates/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    endMileageRate: (id: number) =>
      call<void>(`/api/manage/mileage-rates/${id}`, { method: "DELETE" }),

    listCostCentres: () => call<CostCentre & { active: boolean }[]>("/api/manage/cost-centres"),
    createCostCentre: (body: Partial<CostCentre> & { active?: boolean }) =>
      call<CostCentre>("/api/manage/cost-centres", { method: "POST", body: JSON.stringify(body) }),
    updateCostCentre: (id: number, body: Partial<CostCentre> & { active?: boolean }) =>
      call<CostCentre>(`/api/manage/cost-centres/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    deactivateCostCentre: (id: number) =>
      call<void>(`/api/manage/cost-centres/${id}`, { method: "DELETE" }),

    listManagerOverrides: () => call<ManagerOverride[]>("/api/manage/manager-overrides"),
    upsertManagerOverride: (body: Omit<ManagerOverride, "id" | "set_by_email" | "set_at" | "active">) =>
      call<ManagerOverride>("/api/manage/manager-overrides", { method: "POST", body: JSON.stringify(body) }),
    deactivateManagerOverride: (id: number) =>
      call<void>(`/api/manage/manager-overrides/${id}`, { method: "DELETE" }),

    payrollExportUrl: async (params: { from?: string; to?: string } = {}) => {
      const tok = await getToken();
      const qs = new URLSearchParams();
      if (params.from) qs.set("from", params.from);
      if (params.to) qs.set("to", params.to);
      const res = await fetch(`${API_BASE}/api/manage/payroll/export${qs.toString() ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const blob = await res.blob();
      const filename = res.headers.get("content-disposition")?.match(/filename="?([^";]+)"?/)?.[1] ?? "payroll-export.csv";
      return { blob, filename };
    },
    payrollExportZipUrl: async (params: { from?: string; to?: string } = {}) => {
      const tok = await getToken();
      const qs = new URLSearchParams();
      if (params.from) qs.set("from", params.from);
      if (params.to) qs.set("to", params.to);
      const res = await fetch(`${API_BASE}/api/manage/payroll/export-zip${qs.toString() ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const blob = await res.blob();
      const filename = res.headers.get("content-disposition")?.match(/filename="?([^";]+)"?/)?.[1] ?? "payroll-export.zip";
      return { blob, filename };
    },
    payrollExportMitchXlsxUrl: async (params: { from?: string; to?: string } = {}) => {
      const tok = await getToken();
      const qs = new URLSearchParams();
      if (params.from) qs.set("from", params.from);
      if (params.to) qs.set("to", params.to);
      const res = await fetch(`${API_BASE}/api/manage/payroll/export-mitch-xlsx${qs.toString() ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const blob = await res.blob();
      const filename = res.headers.get("content-disposition")?.match(/filename="?([^";]+)"?/)?.[1] ?? "liaise-expenses.xlsx";
      return { blob, filename };
    },
    payrollExportBacsCsvUrl: async (params: { from?: string; to?: string } = {}) => {
      const tok = await getToken();
      const qs = new URLSearchParams();
      if (params.from) qs.set("from", params.from);
      if (params.to) qs.set("to", params.to);
      const res = await fetch(`${API_BASE}/api/manage/payroll/export-bacs-csv${qs.toString() ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const blob = await res.blob();
      const filename = res.headers.get("content-disposition")?.match(/filename="?([^";]+)"?/)?.[1] ?? "liaise-expenses-bacs.csv";
      return { blob, filename };
    },
    payrollExportIntacctZipUrl: async (params: { from?: string; to?: string; posting_date?: string } = {}) => {
      const tok = await getToken();
      const qs = new URLSearchParams();
      if (params.from) qs.set("from", params.from);
      if (params.to) qs.set("to", params.to);
      if (params.posting_date) qs.set("posting_date", params.posting_date);
      const res = await fetch(`${API_BASE}/api/manage/payroll/export-intacct-zip${qs.toString() ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const blob = await res.blob();
      const filename = res.headers.get("content-disposition")?.match(/filename="?([^";]+)"?/)?.[1] ?? "liaise-expenses-intacct.zip";
      return { blob, filename };
    },
    getEmailPrefs: () => call<{
      notify_payroll_queue: boolean;
      notify_when_to_review: boolean;
      notify_my_approved: boolean;
      notify_my_rejected: boolean;
      notify_my_paid: boolean;
      notify_my_reverted: boolean;
      notify_my_reminders: boolean;
    }>("/api/me/email-prefs"),
    setEmailPrefs: (body: Record<string, boolean>) =>
      call<Record<string, boolean>>("/api/me/email-prefs", { method: "PUT", body: JSON.stringify(body) }),

    listNotificationSettings: () =>
      call<{ notif_key: string; label: string; description: string | null; audience: string; enabled: boolean; updated_at: string }[]>(
        "/api/manage/notification-settings",
      ),
    setNotificationSetting: (key: string, enabled: boolean) =>
      call<{ notif_key: string; label: string; description: string | null; audience: string; enabled: boolean; updated_at: string }>(
        `/api/manage/notification-settings/${encodeURIComponent(key)}`,
        { method: "PUT", body: JSON.stringify({ enabled }) },
      ),

    report: <T = any>(kind: "summary" | "by-home" | "by-category" | "by-claimant", params: { from?: string; to?: string } = {}) => {
      const qs = new URLSearchParams();
      if (params.from) qs.set("from", params.from);
      if (params.to) qs.set("to", params.to);
      return call<T>(`/api/manage/reports/${kind}${qs.toString() ? `?${qs}` : ""}`);
    },

    listRoles: () => call<{ key: string; label: string; description: string; group_id: string; member_count: number | null; error?: string }[]>("/api/manage/roles"),
    listRoleMembers: (role: string) => call<{ role: string; label: string; group_id: string; members: { oid: string; displayName: string; email: string }[] }>(`/api/manage/roles/${role}/members`),
    addRoleMember: (role: string, oid: string) => call<{ ok: true }>(`/api/manage/roles/${role}/members`, { method: "POST", body: JSON.stringify({ oid }) }),
    removeRoleMember: (role: string, oid: string) => call<void>(`/api/manage/roles/${role}/members/${oid}`, { method: "DELETE" }),
  },

  approvals: {
    list: (stage: "manager" | "payroll" | "awaiting-payment" | "history") =>
      call<{ stage: string; items: any[] }>(`/api/approvals?stage=${stage}`),
    approve: (id: string) =>
      call<ExpenseRequestSummary>(`/api/approvals/${id}/actions/approve`, { method: "POST", body: "{}" }),
    reject: (id: string, reason: string, line_flags?: { line_id: string; reason: string }[]) =>
      call<ExpenseRequestSummary>(`/api/approvals/${id}/actions/reject`, { method: "POST", body: JSON.stringify({ reason, line_flags }) }),
    markPaid: (id: string) =>
      call<ExpenseRequestSummary>(`/api/approvals/${id}/actions/mark-paid`, { method: "POST", body: "{}" }),
    revert: (id: string, to_status: "draft" | "submitted" | "manager_approved" | "payroll_approved", reason: string) =>
      call<ExpenseRequestSummary>(`/api/approvals/${id}/actions/revert`, {
        method: "POST",
        body: JSON.stringify({ to_status, reason }),
      }),
    bulk: (
      action: "approve" | "mark-paid" | "delete" | "revert",
      ids: string[],
      extras?: { to_status?: string; reason?: string },
    ) =>
      call<{ ok: string[]; failed: { id: string; reason: string }[]; total: number }>(
        `/api/approvals/bulk/${action}`,
        { method: "POST", body: JSON.stringify({ ids, ...(extras ?? {}) }) },
      ),
    allClaims: (params: { status?: string; from?: string; to?: string; claimant?: string } = {}) => {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => v != null && v !== "" && qs.set(k, String(v)));
      return call<{ items: any[]; total: number }>(`/api/manage/all-claims${qs.toString() ? `?${qs}` : ""}`);
    },
  },
};

// Cross-namespace shortcut: claimantProfile lives under /manage/* (admin oversight)
// but is consumed by the ClaimantProfile page via api.admin. Attach after the literal so it's typed.
(api.admin as any).claimantProfile = (email: string) =>
  call<{
    profile: {
      email: string;
      display_name: string;
      oid?: string;
      manager?: { displayName: string; email: string } | null;
      department?: string | null;
      jobTitle?: string | null;
      officeLocation?: string | null;
      city?: string | null;
      employee_number?: string | null;
    };
    stats: {
      lifetime_count: number;
      lifetime_amount: number;
      paid_amount: number;
      in_flight_amount: number;
      draft_count: number;
      submitted_count: number;
      manager_approved_count: number;
      payroll_approved_count: number;
      paid_count: number;
      rejected_count: number;
      paid_12m: number;
    };
    claims: any[];
  }>(`/api/manage/claimants/${encodeURIComponent(email)}`);

export type { MeResponse } from "./types";
